import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Radio,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import {
  firGetBlocks,
  firAddBlock,
  firUpdateBlock,
  firDeleteBlock,
  firGetUnits,
  firBatchAddUnits,
  firAddUnit,
  firDeleteUnit,
  firListAccountsByBlock,
} from '../firebase';

const { Text } = Typography;

function padValue(val, len = 2) {
  return String(val).padStart(len, '0');
}

/** Inclusive range from a to b — ascending (1→3) or descending (3→1). */
function buildNumericInclusiveRange(a, b) {
  const out = [];
  if (a <= b) {
    for (let i = a; i <= b; i += 1) out.push(i);
  } else {
    for (let i = a; i >= b; i -= 1) out.push(i);
  }
  return out;
}

/** Inclusive letter range — A→D or D→A. */
function buildLetterInclusiveRange(from, to) {
  const start = String(from).toUpperCase().charCodeAt(0);
  const end = String(to).toUpperCase().charCodeAt(0);
  const out = [];
  if (start <= end) {
    for (let c = start; c <= end; c += 1) out.push(c);
  } else {
    for (let c = start; c >= end; c -= 1) out.push(c);
  }
  return out;
}

function generateUnits(levelFrom, levelTo, unitType, unitFrom, unitTo) {
  const units = [];
  const levels = buildNumericInclusiveRange(levelFrom, levelTo);

  levels.forEach((l) => {
    const levelStr = padValue(l);
    if (unitType === 'letters') {
      const charCodes = buildLetterInclusiveRange(unitFrom, unitTo);
      charCodes.forEach((code) => {
        const unitStr = String.fromCharCode(code);
        units.push({
          unitId: `${levelStr}-${unitStr}`,
          level: levelStr,
          unit: unitStr,
        });
      });
    } else {
      const unitNums = buildNumericInclusiveRange(unitFrom, unitTo);
      unitNums.forEach((u) => {
        const unitStr = padValue(u);
        units.push({
          unitId: `${levelStr}-${unitStr}`,
          level: levelStr,
          unit: unitStr,
        });
      });
    }
  });
  return units;
}

/** True if value is missing (allows 0 as valid for numeric ranges). */
function isMissingRangeNumber(v) {
  return v === undefined || v === null || Number.isNaN(v);
}

function isMissingRangeText(v) {
  return v === undefined || v === null || String(v).trim() === '';
}

function AddUnitIdHint({ addUnitForm, blockCode }) {
  const level = Form.useWatch('level', addUnitForm);
  const unit = Form.useWatch('unit', addUnitForm);
  if (!level && !unit) {
    return (
      <Text type="secondary">
        Enter a level and unit to preview the full Unit ID.
      </Text>
    );
  }
  const l = level || '__';
  const u = unit || '__';
  return (
    <Text type="secondary">
      Unit ID (level-unit): {l}-{u} — Full: {blockCode}-{l}-{u}
    </Text>
  );
}

AddUnitIdHint.propTypes = {
  addUnitForm: PropTypes.shape({}).isRequired,
  blockCode: PropTypes.string.isRequired,
};

function modalTitleForMode(mode) {
  if (mode === 'edit') return 'Edit Block';
  if (mode === 'bulkUnits') return 'Bulk Add Units';
  return 'Add New Block';
}

function modalOkTextForMode(mode) {
  if (mode === 'edit') return 'Update';
  if (mode === 'bulkUnits') return 'Add Units';
  return 'Create Block';
}

export default function SiteConfigBlocks({ siteId }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  /** 'create' | 'edit' | 'bulkUnits' */
  const [modalMode, setModalMode] = useState('create');
  const [bulkTargetBlock, setBulkTargetBlock] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [unitsLoading, setUnitsLoading] = useState({});
  const [addUnitModal, setAddUnitModal] = useState({ open: false, blockCode: '' });

  // D24 (2026-05-13): replaces the old Link/Unlink reverse-mapping.
  // For each expanded block we hold a map of unitId → list of accounts
  // whose `assignedSite` + `blockNumber` + `unitNumber` match this unit.
  // Display-only — there is no Link button anymore. Residents are auto-
  // populated as their accounts are created (e.g. when MA approves a
  // D24 account application, the new accounts/{uid} doc has the right
  // blockNumber/unitNumber stamped on it).
  const [accountsByUnit, setAccountsByUnit] = useState({});

  // bulk generator state
  const [generatedPreview, setGeneratedPreview] = useState([]);
  const [unitType, setUnitType] = useState('numbers');

  const [form] = Form.useForm();
  const [addUnitForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeError, typeSuccess, typeWarning } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchBlocks = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const result = await firGetBlocks(nameSite);
        setBlocks(result);
        // D24 (2026-05-13): no success toast on automatic loads — they're
        // noisy and fire twice under React.StrictMode dev. Errors still toast.
      } catch (error) {
        console.error('-- Error fetching blocks:', error);
        openMessage('Error fetching blocks', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  const fetchUnitsForBlock = useCallback(
    async (blockCode) => {
      setUnitsLoading((prev) => ({ ...prev, [blockCode]: true }));
      try {
        // Fetch units list and the block's residents in parallel.
        const [units, accounts] = await Promise.all([
          firGetUnits(siteId, blockCode),
          firListAccountsByBlock(siteId, blockCode),
        ]);
        setExpandedUnits((prev) => ({ ...prev, [blockCode]: units }));
        // Group accounts by unitNumber for fast lookup in the unit table.
        const grouped = {};
        accounts.forEach((a) => {
          const u = a.unitNumber || '';
          if (!grouped[u]) grouped[u] = [];
          grouped[u].push(a);
        });
        setAccountsByUnit((prev) => ({ ...prev, [blockCode]: grouped }));
      } catch (error) {
        console.error('-- Error fetching units/accounts:', error);
        openMessage('Error fetching units', timeoutError, typeError);
      } finally {
        setUnitsLoading((prev) => ({ ...prev, [blockCode]: false }));
      }
    },
    [siteId, openMessage, timeoutError, typeError],
  );

  const handleAddBlock = () => {
    setModalMode('create');
    setBulkTargetBlock(null);
    setEditingBlock(null);
    setGeneratedPreview([]);
    setUnitType('numbers');
    form.resetFields();
    setModalOpen(true);
  };

  const handleEditBlock = (record) => {
    setModalMode('edit');
    setBulkTargetBlock(null);
    setEditingBlock(record);
    setGeneratedPreview([]);
    setUnitType('numbers');
    form.setFieldsValue({ blockName: record.blockName, blockCode: record.id });
    setModalOpen(true);
  };

  const handleDeleteBlock = async (blockCode) => {
    try {
      await firDeleteBlock(siteId, blockCode);
      openMessage('Block deleted', timeoutSuccess, typeSuccess);
      fetchBlocks(siteId);
    } catch (error) {
      console.error('-- Error deleting block:', error);
      openMessage('Error deleting block', timeoutError, typeError);
    }
  };

  // D24 (2026-05-13): destructive-action confirmation that surfaces the
  // currently-assigned residents BEFORE actually deleting. Modal.confirm
  // (rather than the simpler Popconfirm) lets us render a list of impacted
  // accounts so the admin doesn't accidentally orphan residents.
  const confirmDeleteBlock = async (blockCode) => {
    // Block delete can happen without expanding first, so the cache may
    // miss. Always fetch fresh — one query is cheap and the result is
    // accurate at decision time.
    let residents = [];
    try {
      residents = await firListAccountsByBlock(siteId, blockCode);
    } catch (error) {
      console.error('-- Error pre-checking residents before block delete:', error);
      openMessage('Could not check residents — try again', timeoutError, typeError);
      return;
    }
    Modal.confirm({
      title: `Delete block ${blockCode}?`,
      width: 540,
      okText: 'Delete block + all units',
      okType: 'danger',
      cancelText: 'Cancel',
      content: (
        <div>
          <Text type="danger" strong style={{ display: 'block', marginBottom: 8 }}>
            This deletes the block document AND every unit inside it. Cannot be undone.
          </Text>
          {residents.length > 0 ? (
            <>
              <Text strong>
                {residents.length} {pluralise(residents.length, 'resident')} currently registered in this block:
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 8, maxHeight: 220, overflow: 'auto', paddingLeft: 20 }}>
                {residents.map((a) => (
                  <li key={a.id}>
                    <Text>{a.fullname || '—'}</Text>{' '}
                    <Text type="secondary">· {a.email || a.id} · unit {a.unitNumber || '—'}</Text>
                  </li>
                ))}
              </ul>
              <Text type="secondary">
                Their <span className="mono">accounts</span> records are NOT deleted, but{' '}
                <span className="mono">blockNumber</span> / <span className="mono">unitNumber</span>{' '}
                will point at a non-existent block until you reassign them.
              </Text>
            </>
          ) : (
            <Text>No residents currently registered in this block. Safe to delete.</Text>
          )}
        </div>
      ),
      onOk: () => handleDeleteBlock(blockCode),
    });
  };

  // Builds the unit list from current form ranges. Returns [] if any range is
  // missing/invalid. Used as a fallback in handleModalOk so users don't silently
  // lose units when they forget to click Generate Preview.
  const autoGenerateFromForm = () => {
    const values = form.getFieldsValue(true);
    const { levelFrom, levelTo, unitFrom, unitTo } = values;
    if (isMissingRangeNumber(levelFrom) || isMissingRangeNumber(levelTo)) return [];
    if (unitType === 'numbers') {
      if (isMissingRangeNumber(unitFrom) || isMissingRangeNumber(unitTo)) return [];
    } else if (isMissingRangeText(unitFrom) || isMissingRangeText(unitTo)) {
      return [];
    }
    return generateUnits(levelFrom, levelTo, unitType, unitFrom, unitTo);
  };

  const handleModalOk = async () => {
    try {
      if (modalMode === 'edit') {
        const values = await form.validateFields(['blockName']);
        await firUpdateBlock(siteId, editingBlock.id, { blockName: values.blockName });
        openMessage('Block updated', timeoutSuccess, typeSuccess);
      } else if (modalMode === 'bulkUnits') {
        if (!bulkTargetBlock?.id) {
          openMessage('Missing block', timeoutWarning, typeWarning);
          return;
        }
        // If user filled ranges but forgot to click Generate Preview, auto-generate
        // so units aren't silently dropped.
        const effectivePreview =
          generatedPreview.length > 0 ? generatedPreview : autoGenerateFromForm();
        if (effectivePreview.length === 0) {
          openMessage(
            'Set level/unit ranges and click Generate Preview first',
            timeoutWarning,
            typeWarning,
          );
          return;
        }
        await firBatchAddUnits(siteId, bulkTargetBlock.id, effectivePreview);
        openMessage(
          `Added ${effectivePreview.length} ${pluralise(effectivePreview.length, 'unit')}`,
          timeoutSuccess,
          typeSuccess,
        );
        fetchUnitsForBlock(bulkTargetBlock.id);
      } else {
        const values = await form.validateFields(['blockName', 'blockCode']);
        const { blockName, blockCode: codeRaw } = values;
        const blockCode = String(codeRaw).trim();

        // Auto-generate preview from ranges if user didn't click Generate Preview.
        const effectivePreview =
          generatedPreview.length > 0 ? generatedPreview : autoGenerateFromForm();

        await firAddBlock(siteId, blockCode, {
          blockName,
          createdAt: new Date().toISOString().split('T')[0],
        });
        if (effectivePreview.length > 0) {
          await firBatchAddUnits(siteId, blockCode, effectivePreview);
          openMessage(
            `Block created with ${effectivePreview.length} ${pluralise(effectivePreview.length, 'unit')}`,
            timeoutSuccess,
            typeSuccess,
          );
          // Pre-warm the expandable row so units appear on first +-click without
          // needing a collapse/re-expand to trigger a re-render.
          fetchUnitsForBlock(blockCode);
        } else {
          openMessage('Block created', timeoutSuccess, typeSuccess);
        }
      }
      setModalOpen(false);
      form.resetFields();
      setGeneratedPreview([]);
      setBulkTargetBlock(null);
      setModalMode('create');
      fetchBlocks(siteId);
    } catch (error) {
      if (error.errorFields) return;
      console.error('-- Error saving block:', error);
      openMessage(
        error?.message || 'Error saving. Check Firestore rules and console.',
        timeoutError,
        typeError,
      );
    }
  };

  const handleGeneratePreview = () => {
    const values = form.getFieldsValue(true);
    const blockCodeRaw = values.blockCode;
    const blockCode =
      typeof blockCodeRaw === 'string' ? blockCodeRaw.trim() : blockCodeRaw;
    const { levelFrom, levelTo, unitFrom, unitTo } = values;

    if (modalMode === 'create' && (!blockCode || String(blockCode).trim() === '')) {
      openMessage('Please enter a block code first', timeoutWarning, typeWarning);
      return;
    }
    if (modalMode === 'bulkUnits' && !bulkTargetBlock?.id) {
      openMessage('Missing block', timeoutWarning, typeWarning);
      return;
    }

    if (isMissingRangeNumber(levelFrom) || isMissingRangeNumber(levelTo)) {
      openMessage('Please fill in level from and to', timeoutWarning, typeWarning);
      return;
    }

    if (unitType === 'numbers') {
      if (isMissingRangeNumber(unitFrom) || isMissingRangeNumber(unitTo)) {
        openMessage('Please fill in unit from and to', timeoutWarning, typeWarning);
        return;
      }
    } else if (isMissingRangeText(unitFrom) || isMissingRangeText(unitTo)) {
      openMessage('Please fill in unit from and to (letters)', timeoutWarning, typeWarning);
      return;
    }

    const units = generateUnits(levelFrom, levelTo, unitType, unitFrom, unitTo);
    setGeneratedPreview(units);
    openMessage(
      `Preview: ${units.length} ${pluralise(units.length, 'unit')}`,
      timeoutSuccess,
      typeSuccess,
    );
  };

  const handleRemoveFromPreview = (unitId) => {
    setGeneratedPreview((prev) => prev.filter((u) => u.unitId !== unitId));
  };

  const handleDeleteUnit = async (blockCode, unitId) => {
    try {
      await firDeleteUnit(siteId, blockCode, unitId);
      openMessage('Unit deleted', timeoutSuccess, typeSuccess);
      fetchUnitsForBlock(blockCode);
    } catch (error) {
      console.error('-- Error deleting unit:', error);
      openMessage('Error deleting unit', timeoutError, typeError);
    }
  };

  const confirmDeleteUnit = (blockCode, unitId) => {
    // Unit delete only happens inside the expanded view, so the cache
    // is guaranteed to be populated for this block.
    const residents = (accountsByUnit[blockCode] || {})[unitId] || [];
    Modal.confirm({
      title: `Delete unit ${unitId}?`,
      width: 480,
      okText: 'Delete unit',
      okType: 'danger',
      cancelText: 'Cancel',
      content: (
        <div>
          {residents.length > 0 ? (
            <>
              <Text type="danger" strong>
                {residents.length} {pluralise(residents.length, 'resident')} currently registered in this unit:
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                {residents.map((a) => (
                  <li key={a.id}>
                    <Text>{a.fullname || '—'}</Text>{' '}
                    <Text type="secondary">· {a.email || a.id}</Text>
                  </li>
                ))}
              </ul>
              <Text type="secondary">
                Their <span className="mono">accounts</span> records are NOT deleted, but{' '}
                <span className="mono">unitNumber</span> will point at a non-existent unit until you reassign them.
              </Text>
            </>
          ) : (
            <Text>No residents currently registered. Safe to delete.</Text>
          )}
        </div>
      ),
      onOk: () => handleDeleteUnit(blockCode, unitId),
    });
  };

  const handleAddSingleUnit = async () => {
    try {
      const values = await addUnitForm.validateFields();
      const { level, unit } = values;
      const unitId = `${level}-${unit}`;
      await firAddUnit(siteId, addUnitModal.blockCode, { unitId, level, unit });
      openMessage('Unit added', timeoutSuccess, typeSuccess);
      addUnitForm.resetFields();
      setAddUnitModal({ open: false, blockCode: '' });
      fetchUnitsForBlock(addUnitModal.blockCode);
    } catch (error) {
      if (error.errorFields) return;
      console.error('-- Error adding unit:', error);
      openMessage('Error adding unit', timeoutError, typeError);
    }
  };

  const handleBulkAddToExisting = (record) => {
    setModalMode('bulkUnits');
    setBulkTargetBlock({ id: record.id, blockName: record.blockName });
    setEditingBlock(null);
    setGeneratedPreview([]);
    setUnitType('numbers');
    form.resetFields();
    form.setFieldsValue({
      blockCode: record.id,
      blockName: record.blockName,
    });
    setModalOpen(true);
  };

  useEffect(() => {
    fetchBlocks(siteId);
  }, [fetchBlocks, siteId]);

  const columns = [
    {
      title: 'Block Name',
      dataIndex: 'blockName',
      width: 200,
    },
    {
      title: 'Block Code',
      dataIndex: 'id',
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      width: 140,
    },
    {
      title: 'Actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditBlock(record)}
            size="small"
            type="link"
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            type="link"
            onClick={() => confirmDeleteBlock(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const unitColumns = (blockCode) => [
    {
      title: 'Unit ID',
      dataIndex: 'id',
      width: 140,
      sorter: (a, b) => a.id.localeCompare(b.id),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Level',
      dataIndex: 'level',
      width: 100,
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      width: 100,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      width: 140,
    },
    {
      title: 'Residents',
      width: 240,
      render: (_, record) => {
        // D24 (2026-05-13): derived display, no Link button. Residents
        // are accounts whose assignedSite + blockNumber + unitNumber match
        // this unit, sourced once-per-block via firListAccountsByBlock and
        // grouped client-side into accountsByUnit[blockCode][unitId].
        const blockResidents = accountsByUnit[blockCode] || {};
        const residents = blockResidents[record.id] || [];
        if (residents.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        const popoverContent = (
          <Space direction="vertical" style={{ maxWidth: 360 }}>
            {residents.map((a) => (
              <Flex key={a.id} vertical>
                <Text strong>{a.fullname || '—'}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {a.email || a.id}
                </Text>
              </Flex>
            ))}
          </Space>
        );
        return (
          <Popover content={popoverContent} title={`Residents in ${record.id}`}>
            <Tag color="blue" style={{ cursor: 'pointer' }}>
              {residents.length} {pluralise(residents.length, 'resident')}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          size="small"
          type="link"
          onClick={() => confirmDeleteUnit(blockCode, record.id)}
        />
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const blockCode = record.id;
    const units = expandedUnits[blockCode] || [];
    const isLoading = unitsLoading[blockCode] || false;

    return (
      <div style={{ padding: '8px 0' }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <Text strong>{units.length} {pluralise(units.length, 'unit')}</Text>
          <Space>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={() => handleBulkAddToExisting(record)}
              size="small"
            >
              Bulk Add Units
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddUnitModal({ open: true, blockCode })}
              size="small"
              type="primary"
            >
              Add Unit
            </Button>
          </Space>
        </Flex>
        <Table
          columns={unitColumns(blockCode)}
          dataSource={units}
          loading={isLoading}
          pagination={{ pageSize: 20, size: 'small', showSizeChanger: true }}
          rowKey="id"
          scroll={{ x: 960 }}
          size="small"
        />
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Flex justify="flex-end" style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={handleAddBlock} type="primary">
          Add Block
        </Button>
      </Flex>
      <Table
        columns={columns}
        dataSource={blocks}
        expandable={{
          expandedRowRender,
          onExpand: (expanded, record) => {
            if (expanded) fetchUnitsForBlock(record.id);
          },
        }}
        loading={loading}
        pagination={false}
        rowKey="id"
        scroll={{ y: scrollHeight }}
      />

      {/* Add/Edit Block Modal */}
      <Modal
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setGeneratedPreview([]);
          setBulkTargetBlock(null);
          setModalMode('create');
        }}
        okText={modalOkTextForMode(modalMode)}
        title={modalTitleForMode(modalMode)}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          {modalMode === 'bulkUnits' ? (
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                marginBottom: 16,
                padding: '8px 12px',
              }}
            >
              <Text type="secondary">Adding units to: </Text>
              <Text strong>
                {bulkTargetBlock?.blockName || '--'}{' '}
                <span className="mono">({bulkTargetBlock?.id})</span>
              </Text>
            </div>
          ) : (
            <Row gutter={16}>
              <Col span={14}>
                <Form.Item
                  label="Block Name"
                  name="blockName"
                  rules={[{ required: true, message: 'Please enter a block name' }]}
                >
                  <Input placeholder="e.g., Block 1, Tower A" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Block Code"
                  name="blockCode"
                  rules={[{ required: true, message: 'Please enter a block code' }]}
                  tooltip="Short code used as prefix in unit IDs"
                >
                  <Input
                    disabled={modalMode === 'edit'}
                    placeholder="e.g., 01, 21"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {(modalMode === 'create' || modalMode === 'bulkUnits') && (
            <>
              <Divider orientation="left" plain>
                Bulk Unit Generator (optional)
              </Divider>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Level and unit ranges can go either way — e.g. level 20 to 1, or units 12 to 1.
              </Text>

              <Form.Item label="Level Range">
                <Row gutter={8}>
                  <Col span={10}>
                    <Form.Item name="levelFrom" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="From"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    span={4}
                    style={{ textAlign: 'center', lineHeight: '32px' }}
                  >
                    to
                  </Col>
                  <Col span={10}>
                    <Form.Item name="levelTo" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="To"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item label="Unit Labels (per level)">
                <Radio.Group
                  onChange={(e) => setUnitType(e.target.value)}
                  value={unitType}
                  style={{ marginBottom: 8 }}
                >
                  <Radio value="numbers">Numbers</Radio>
                  <Radio value="letters">Letters</Radio>
                </Radio.Group>
                <Row gutter={8}>
                  <Col span={10}>
                    <Form.Item name="unitFrom" noStyle>
                      {unitType === 'numbers' ? (
                        <InputNumber
                          min={0}
                          placeholder="From"
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <Input placeholder="e.g., A" maxLength={2} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col
                    span={4}
                    style={{ textAlign: 'center', lineHeight: '32px' }}
                  >
                    to
                  </Col>
                  <Col span={10}>
                    <Form.Item name="unitTo" noStyle>
                      {unitType === 'numbers' ? (
                        <InputNumber
                          min={0}
                          placeholder="To"
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <Input placeholder="e.g., D" maxLength={2} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleGeneratePreview}
                style={{ marginBottom: 12 }}
              >
                Generate Preview
              </Button>

              {generatedPreview.length > 0 && (
                <div
                  style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    maxHeight: 200,
                    overflow: 'auto',
                    padding: 12,
                  }}
                >
                  <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                    {generatedPreview.length} units to be created:
                  </Text>
                  <Flex wrap="wrap" gap={4}>
                    {generatedPreview.map((u) => (
                      <Tag
                        key={u.unitId}
                        closable
                        onClose={() => handleRemoveFromPreview(u.unitId)}
                      >
                        {u.unitId}
                      </Tag>
                    ))}
                  </Flex>
                </div>
              )}
            </>
          )}
        </Form>
      </Modal>

      {/* Add Single Unit Modal */}
      <Modal
        title={`Add Unit to Block ${addUnitModal.blockCode}`}
        open={addUnitModal.open}
        onOk={handleAddSingleUnit}
        onCancel={() => {
          setAddUnitModal({ open: false, blockCode: '' });
          addUnitForm.resetFields();
        }}
        okText="Add Unit"
        destroyOnClose
      >
        <Form form={addUnitForm} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Level"
                name="level"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="e.g., 08, PH" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Unit"
                name="unit"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="e.g., 07, A" />
              </Form.Item>
            </Col>
          </Row>
          <AddUnitIdHint addUnitForm={addUnitForm} blockCode={addUnitModal.blockCode} />
        </Form>
      </Modal>
    </>
  );
}

SiteConfigBlocks.propTypes = {
  siteId: PropTypes.string.isRequired,
};
